import { useMemo, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material'
import { IInvoiceWithCustomer } from './statistics'

interface UpwardTrajectoryCustomersTableProps {
  invoices: IInvoiceWithCustomer[]
}

export default function UpwardTrajectoryCustomersTable({
  invoices,
}: UpwardTrajectoryCustomersTableProps) {
  // Group invoices by customer
  const [sortBy, setSortBy] = useState<
    | 'pctTotal'
    | 'pctCount'
    | 'firstTotal'
    | 'lastTotal'
    | 'firstCount'
    | 'lastCount'
    | 'n'
    | 'daysSinceLast'
    | 'avgTotal'
    | 'avgCount'
  >('pctTotal')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const customerTrends = useMemo(() => {
    const map: {
      [customerId: string]: {
        name: string
        dates: number[]
        totals: number[]
        itemCounts: number[]
      }
    } = {}
    invoices.forEach((inv) => {
      const cust = inv.tblCustomer
      if (!cust || typeof cust.customerId === 'undefined') return
      const customerId = String(cust.customerId)
      let customerName = ''
      if (typeof cust.customerName === 'string')
        customerName = cust.customerName
      else if (typeof cust.name === 'string') customerName = cust.name
      const date = new Date(inv.createdAt).getTime()
      const total = Number(inv.grandTotal ?? inv.total ?? inv.amount ?? 0)
      const itemCount = Array.isArray(inv.invoiceProducts)
        ? inv.invoiceProducts.reduce(
            (sum: number, prod) => sum + (Number(prod.productQty) || 0),
            0
          )
        : 0
      if (!map[customerId]) {
        map[customerId] = {
          name: customerName,
          dates: [],
          totals: [],
          itemCounts: [],
        }
      }
      map[customerId].dates.push(date)
      map[customerId].totals.push(total)
      map[customerId].itemCounts.push(itemCount)
    })

    return (
      Object.entries(map)
        .map(([id, { name, dates, totals, itemCounts }]) => {
          // Sort by date
          const sorted = dates
            .map((d, i) => ({ d, t: totals[i], c: itemCounts[i] }))
            .sort((a, b) => a.d - b.d)
          const xs = sorted.map((v, i) => i) // use index as x
          const ysTotal = sorted.map((v) => v.t)
          const ysCount = sorted.map((v) => v.c)
          const firstTotal = ysTotal[0] || 0
          const lastTotal = ysTotal[ysTotal.length - 1] || 0
          const firstCount = ysCount[0] || 0
          const lastCount = ysCount[ysCount.length - 1] || 0
          const pctTotal =
            firstTotal === 0
              ? null
              : ((lastTotal - firstTotal) / Math.abs(firstTotal)) * 100
          const pctCount =
            firstCount === 0
              ? null
              : ((lastCount - firstCount) / Math.abs(firstCount)) * 100
          // Days since last order
          const lastDate =
            sorted.length > 0 ? sorted[sorted.length - 1].d : null
          let daysSinceLast = null
          if (lastDate) {
            const now = new Date()
            daysSinceLast = Math.floor(
              (now.getTime() - lastDate) / (1000 * 60 * 60 * 24)
            )
          }
          // Average order nominal and item count
          const avgTotal =
            ysTotal.length > 0
              ? ysTotal.reduce((a, b) => a + b, 0) / ysTotal.length
              : 0
          const avgCount =
            ysCount.length > 0
              ? ysCount.reduce((a, b) => a + b, 0) / ysCount.length
              : 0
          return {
            id,
            name,
            n: xs.length,
            pctTotal,
            pctCount,
            firstTotal,
            lastTotal,
            firstCount,
            lastCount,
            daysSinceLast,
            avgTotal,
            avgCount,
          }
        })
        // Only keep those with upward trend in either metric and at least 3 invoices
        .filter((c) => c.n >= 3)
        // Sort by selected column
        .sort((a, b) => {
          let vA = a[sortBy] ?? 0
          let vB = b[sortBy] ?? 0
          // All sortable columns are numbers, so skip string conversion
          if (vA < vB) return sortDir === 'asc' ? -1 : 1
          if (vA > vB) return sortDir === 'asc' ? 1 : -1
          return 0
        })
    )
  }, [invoices, sortBy, sortDir])

  if (customerTrends.length === 0) {
    return <Typography>Tidak ada customer dengan tren naik.</Typography>
  }

  const handleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortBy(col)
      setSortDir('desc')
    }
  }

  return (
    <div>
      <TableContainer component={Paper} style={{ marginTop: 32 }}>
        <Typography variant="h6" style={{ padding: 16 }}>
          Customers with Upward Order Trajectory
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer Name</TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('n')}
              >
                # of Invoices{' '}
                {sortBy === 'n' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('daysSinceLast')}
              >
                Days Since Last Order{' '}
                {sortBy === 'daysSinceLast'
                  ? sortDir === 'asc'
                    ? '▲'
                    : '▼'
                  : ''}
              </TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('firstTotal')}
              >
                First Order Nominal{' '}
                {sortBy === 'firstTotal' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('lastTotal')}
              >
                Last Order Nominal{' '}
                {sortBy === 'lastTotal' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('avgTotal')}
              >
                Avg Order Nominal{' '}
                {sortBy === 'avgTotal' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('firstCount')}
              >
                First Order Item Count{' '}
                {sortBy === 'firstCount' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('lastCount')}
              >
                Last Order Item Count{' '}
                {sortBy === 'lastCount' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('avgCount')}
              >
                Avg Order Item Count{' '}
                {sortBy === 'avgCount' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
              {/* Removed slope columns */}
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('pctTotal')}
              >
                % Increase Nominal{' '}
                {sortBy === 'pctTotal' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
              <TableCell
                align="right"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('pctCount')}
              >
                % Increase Items{' '}
                {sortBy === 'pctCount' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customerTrends.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell align="right">{row.n}</TableCell>
                <TableCell align="right">
                  {row.daysSinceLast == null ? '-' : row.daysSinceLast}
                </TableCell>
                <TableCell align="right">
                  {row.firstTotal.toLocaleString('id-ID')}
                </TableCell>
                <TableCell align="right">
                  {row.lastTotal.toLocaleString('id-ID')}
                </TableCell>
                <TableCell align="right">
                  {row.avgTotal.toLocaleString('id-ID', {
                    maximumFractionDigits: 0,
                  })}
                </TableCell>
                <TableCell align="right">{row.firstCount}</TableCell>
                <TableCell align="right">{row.lastCount}</TableCell>
                <TableCell align="right">{row.avgCount.toFixed(1)}</TableCell>
                {/* Removed slope columns */}
                <TableCell align="right">
                  {row.pctTotal === null
                    ? '-'
                    : `${row.pctTotal >= 0 ? '+' : ''}${row.pctTotal.toFixed(
                        1
                      )}%`}
                </TableCell>
                <TableCell align="right">
                  {row.pctCount === null
                    ? '-'
                    : `${row.pctCount >= 0 ? '+' : ''}${row.pctCount.toFixed(
                        1
                      )}%`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}
